import { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import axios from "axios";
import { Form, Button, Spinner, Alert, Card } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload, faRedo } from "@fortawesome/free-solid-svg-icons";
import Plot from 'react-plotly.js';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const RawDataView = () => {
    const { measurementId, auth, onLogout } = useOutletContext(); // with auth and onLogout
    const [measurement, setMeasurement] = useState(null);
    const [plotData, setPlotData] = useState({});
    const [fileContent, setFileContent] = useState("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [textStyles, setTextStyles] = useState({ fontSize: "14px", lineHeight: "1.6", fontColor: "#000000", backgroundColor: "#ffffff", fontFamily: "monospace" });

    const fetchData = async () => {
        try {
            setLoading(true);
            setError(null);

            const headers = { Authorization: `Bearer ${auth.token}` };

            const measurementRes = await axios.get(`${API_BASE_URL}/measurements/${measurementId}`, { headers });
            setMeasurement(measurementRes.data);

            const plotDataRes = await axios.get(`${API_BASE_URL}/graphs/${measurementId}/plot`, { headers });
            const { x, series } = plotDataRes.data;
            const plotlyData = series.map((s) => ({
                x,
                y: s.data,
                type: 'scatter',
                mode: 'lines',
                name: s.name
            }));
            setPlotData(plotlyData);

            const fileRes = await axios.get(`${API_BASE_URL}/files/file`, {
                params: { path: `${measurementRes.data.user}/${measurementRes.data.filename}` },
                headers
            });

            if (fileRes.data && fileRes.data.content) {
                setFileContent(fileRes.data.content);
            } else {
                throw new Error("File content not found in response");
            }
        } catch (err) {
            if (err.response?.status === 401) {
                onLogout(); // Trigger logout on 401 error
            } else {
                setError(err.response?.data?.message || err.message || "Failed to load raw data");
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (measurementId && auth.token) {
            fetchData();
        } else {
            setError("Invalid measurement ID or authentication token");
            setLoading(false);
        }
    }, [measurementId, auth.token]);

    const handleDownload = () => {
        const blob = new Blob([fileContent], { type: "text/plain" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = measurement?.filename || "measurement.txt";
        a.click();
        URL.revokeObjectURL(url);
    };

    const handleDownloadGraphData = () => {
        if (!plotData || plotData.length === 0) return;

        // Convert plot data to CSV format
        let csvContent = "timestamp";

        // Add headers for each series
        plotData.forEach(series => { csvContent += `,${series.name}` });
        csvContent += "\n";

        const timestamps = plotData[0].x;

        // Add data rows
        timestamps.forEach((timestamp, index) => {
            csvContent += timestamp;
            plotData.forEach(series => {
                csvContent += `,${series.y[index]}`;
            });
            csvContent += "\n";
        });

        // Create and trigger download
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = measurement ? `${measurement.filename.replace(/\.[^/.]+$/, "")}_graph.csv` : "graph_data.csv";
        a.click();
        URL.revokeObjectURL(url);
    };

    const handleStyleChange = (e) => {
        const { name, value } = e.target;
        setTextStyles((prev) => ({ ...prev, [name]: value }));
    };

    const handleRetry = () => {
        setError(null);
        fetchData();
    };

    return (
        <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
                <div>
                    {measurement && (
                        <p className="mb-1">
                            <strong>File path:</strong> {measurement.user}/{measurement.filename}
                        </p>
                    )}
                </div>
                <div>
                    <Button variant="primary" size="sm" onClick={handleDownload} disabled={!fileContent} className="m-1 p-2">
                        <FontAwesomeIcon icon={faDownload} className="me-1" /> Download as .txt
                    </Button>
                    <Button variant="success" size="sm" onClick={handleDownloadGraphData} disabled={!plotData || plotData.length === 0} className="m-1 p-2">
                        <FontAwesomeIcon icon={faDownload} className="me-1" /> Download as .csv
                    </Button>
                    {error && (
                        <Button variant="outline-danger" size="sm" onClick={handleRetry}>
                            <FontAwesomeIcon icon={faRedo} className="me-1" /> Retry
                        </Button>
                    )}
                </div>
            </Card.Header>
            <Card.Body>
                {loading && <Spinner animation="border"/>}

                {error && (
                    <Alert variant="danger" className="mb-3"> {error} </Alert>
                )}

                {plotData && (
                    <Plot
                        data={plotData}
                        layout={{
                            title: 'Sensor Data Visualization',
                            xaxis: { title: 'Timestamp' },
                            yaxis: { title: 'Values' },
                            autosize: true,
                            showlegend: true
                        }}
                        style={{ width: '100%', height: '80vh' }}
                    />
                )}

                <div className="mt-4 p-3 bg-light rounded">
                    <h5>Display Options</h5>
                    <Form className="row g-3">
                        <Form.Group className="col-md-2">
                            <Form.Label>Font Size</Form.Label>
                            <Form.Select name="fontSize" value={textStyles.fontSize} onChange={handleStyleChange}>
                                {[12, 14, 16, 18].map((size) => (
                                    <option key={size} value={`${size}px`}> {size}px </option>
                                ))}
                            </Form.Select>
                        </Form.Group>

                        <Form.Group className="col-md-2">
                            <Form.Label>Font Family</Form.Label>
                            <Form.Select name="fontFamily" value={textStyles.fontFamily} onChange={handleStyleChange}>
                                <option value="monospace">Monospace</option>
                                <option value="Tahoma, sans-serif">Tahoma</option>
                                <option value="Lucida Console, monospace">Lucida Console</option>
                            </Form.Select>
                        </Form.Group>

                        <Form.Group className="col-md-2">
                            <Form.Label>Line Height</Form.Label>
                            <Form.Select name="lineHeight" value={textStyles.lineHeight} onChange={handleStyleChange}>
                                <option value="1.6">1.6</option>
                                <option value="1.8">1.8</option>
                                <option value="2.0">2.0</option>
                            </Form.Select>
                        </Form.Group>

                        <Form.Group className="col-md-1">
                            <Form.Label>Text Color</Form.Label>
                            <Form.Control type="color" name="fontColor" value={textStyles.fontColor} onChange={handleStyleChange}/>
                        </Form.Group>

                        <Form.Group className="col-md-1">
                            <Form.Label>Background</Form.Label>
                            <Form.Control type="color" name="backgroundColor" value={textStyles.backgroundColor} onChange={handleStyleChange}/>
                        </Form.Group>

                        <div className="col-md-1 d-flex align-items-end">
                            <Button variant="outline-secondary"
                                onClick={() => setTextStyles({
                                    fontSize: "14px",
                                    lineHeight: "1.5",
                                    fontColor: "#000000",
                                    backgroundColor: "#ffffff",
                                    fontFamily: "monospace",
                                })}
                            > Reset
                            </Button>
                        </div>
                    </Form>
                </div>

                <div className="border rounded p-3 mt-4">
                    <pre style={{
                        whiteSpace: "pre-wrap",
                        fontFamily: textStyles.fontFamily,
                        fontSize: textStyles.fontSize,
                        lineHeight: textStyles.lineHeight,
                        color: textStyles.fontColor,
                        backgroundColor: textStyles.backgroundColor,
                        borderRadius: "0.25rem",
                        maxHeight: "75vh",
                        overflowY: "auto",
                    }}
                    >
                        {fileContent || (loading ? "Loading..." : "No file content available")}
                    </pre>
                </div>
            </Card.Body>
        </Card>
    );
};

export default RawDataView;